import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, Box, Typography, Button, IconButton, Divider, alpha, CircularProgress, Zoom, TextField,
  Chip, Tooltip, InputAdornment, useMediaQuery,
  Table, TableBody, TableCell, TableHead, TableRow,
} from '@mui/material';
import dayjs from 'dayjs';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded';
import ShoppingCartRoundedIcon from '@mui/icons-material/ShoppingCartRounded';
import PaidRoundedIcon from '@mui/icons-material/PaidRounded';
import LocalOfferRoundedIcon from '@mui/icons-material/LocalOfferRounded';
import AddCircleRoundedIcon from '@mui/icons-material/AddCircleRounded';
import SavingsRoundedIcon from '@mui/icons-material/SavingsRounded';
import QrCode2RoundedIcon from '@mui/icons-material/QrCode2Rounded';
import CreditCardRoundedIcon from '@mui/icons-material/CreditCardRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import MoreHorizRoundedIcon from '@mui/icons-material/MoreHorizRounded';
import PieChartRoundedIcon from '@mui/icons-material/PieChartRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import CategoryRoundedIcon from '@mui/icons-material/CategoryRounded';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded';
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded';
import EventRoundedIcon from '@mui/icons-material/EventRounded';
import PendingActionsRoundedIcon from '@mui/icons-material/PendingActionsRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import {
  SessaoCaixaService, IResumoVendasSessao, IVendaResumoPDV, IItemVendaPdv,
} from '../../shared/services/api/Venda/SessaoCaixaService';
import { PDFResumoVendasPDV } from './impressaocaixa';
import { PDVThemeColors } from './PDVThemes';
import { DialogAutorizacaoGerente } from './DialogAutorizacaoGerente';
import { DialogItensVendaPDV } from './DialogItensVendaPDV';

// Item do carrinho ainda não finalizado (mesmo formato de `Product`, em
// Pdv.tsx — não importado direto pra não criar uma dependência circular de
// tipos entre os dois arquivos; só os campos usados aqui mesmo).
export interface IItemVendaPendente {
  descricao: string;
  qtds: number;
  preco: number;
  excluido?: boolean;
}

interface DialogResumoVendasPDVProps {
  open: boolean;
  onClose: () => void;
  nomeCaixa: string;
  nomeOperador: string;
  dataabertura: string;
  colors: PDVThemeColors;
  onImprimir: (blob: Blob, titulo: string) => void;
  // Carrinho atual, ainda não finalizado (ver chaveVendaPendente em
  // Pdv.tsx) — mostrado destacado no topo do conteúdo (ver
  // itensPendentesAtivos logo no início do componente), pra o operador
  // achar o caminho de volta pra ela mesmo depois de navegar pra essa tela.
  // Array completo, incluindo itens excluídos — só a exibição filtra os
  // ativos.
  vendaPendenteItens: IItemVendaPendente[];
  // "Continuar esta venda" — o carrinho em si (Pdv.tsx) já É a venda
  // pendente mostrada aqui (mesmo array, ver vendaPendenteItens); só fecha
  // essa tela e devolve o foco pro carrinho, sem precisar ir/voltar dados.
  onRestaurarVendaPendente: () => void;
  // Ícone de lixeira no card da venda pendente — descarta o carrinho de
  // vez (com confirmação inline, ver confirmandoExclusaoPendente). Nunca
  // entra em nenhum total desta tela (ver itensPendentesAtivos/
  // totalVendaPendente) — só existe pra dar um jeito de voltar nela ou
  // descartá-la, não conta como venda de verdade enquanto não for
  // finalizada.
  onExcluirVendaPendente: () => void;
  // "Editar venda" — diferente de cancelamento: NÃO cancela/estorna nada aqui
  // (só carrega os itens no carrinho pra mexer). Pdv.tsx entra em modo de
  // edição; o update na venda de verdade só acontece quando o operador
  // finalizar no Dialogvenda.tsx (ver idVendaParaEditar lá).
  onEditarVendaIniciada: (idmov: string, numero: string, itens: IItemVendaPdv[]) => void;
}

const BADGE_STATUS: Record<'cancelada' | 'devolvida', { label: string; cor: string }> = {
  cancelada: { label: 'Cancelada', cor: '#D32F2F' },
  devolvida: { label: 'Devolvida', cor: '#F57C00' },
};

const formatMoeda = (valor: number) =>
  `R$ ${Number(valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Ícone/cor por categoria de forma de pagamento — não é uma lista fechada
// (o backend devolve a categoria salva na venda, ver FormasPagamento.ts no
// backend, que aceita qualquer texto), então casa por trecho do nome em vez
// de comparar igualdade exata, e cai num visual neutro ("Outros") pra
// qualquer categoria não reconhecida.
const getFormaPagamentoVisual = (categoria: string): { icon: React.ReactNode; cor: string } => {
  const texto = categoria.toLowerCase();
  if (texto.includes('dinheiro')) return { icon: <PaidRoundedIcon sx={{ fontSize: 16 }} />, cor: '#388E3C' };
  if (texto.includes('pix')) return { icon: <QrCode2RoundedIcon sx={{ fontSize: 16 }} />, cor: '#0D9488' };
  if (texto.includes('déb') || texto.includes('deb')) return { icon: <CreditCardRoundedIcon sx={{ fontSize: 16 }} />, cor: '#1976D2' };
  if (texto.includes('créd') || texto.includes('cred') || texto.includes('cart')) return { icon: <CreditCardRoundedIcon sx={{ fontSize: 16 }} />, cor: '#7B1FA2' };
  if (texto.includes('prazo') || texto.includes('crediár') || texto.includes('crediar') || texto.includes('fatur')) return { icon: <ReceiptLongRoundedIcon sx={{ fontSize: 16 }} />, cor: '#F57C00' };
  return { icon: <MoreHorizRoundedIcon sx={{ fontSize: 16 }} />, cor: '#616161' };
};

// Card do topo (Vendas realizadas/Valor bruto/Descontos/Acréscimos/Total
// vendido/Ticket médio) — mesmo espírito do CardResumo do Fluxo de Caixa
// (ícone colorido + label + valor), com um badge de ícone (não só a cor do
// texto) pra bater com o mockup de referência.
const CardMetrica: React.FC<{ label: string; valor: string; icon: React.ReactNode; cor: string; destaque?: boolean }> = ({
  label, valor, icon, cor, destaque,
}) => (
  <Box
    sx={{
      flex: '1 1 150px',
      minWidth: 130,
      p: 1.5,
      borderRadius: '12px',
      border: `1px solid ${alpha(cor, destaque ? 0.35 : 0.16)}`,
      bgcolor: destaque ? alpha(cor, 0.08) : alpha(cor, 0.03),
      display: 'flex',
      flexDirection: 'column',
      gap: 0.75,
    }}
  >
    <Box sx={{
      width: 30, height: 30, borderRadius: '8px', bgcolor: alpha(cor, 0.15),
      display: 'flex', alignItems: 'center', justifyContent: 'center', color: cor,
    }}>
      {icon}
    </Box>
    <Typography variant="caption" color="text.secondary" fontWeight={600}>{label}</Typography>
    <Typography variant={destaque ? 'h6' : 'subtitle1'} fontWeight={700} sx={{ color: destaque ? cor : 'text.primary' }} noWrap>
      {valor}
    </Typography>
  </Box>
);

// Chips de forma de pagamento de uma linha da relação de vendas — pagamento
// misto (mais de uma forma na mesma venda) mostra "Dinheiro + PIX"; passar do
// mouse mostra o valor de cada uma (ver doc, item 11: não poluir a tabela
// principal com os valores).
const ChipsPagamento: React.FC<{ pagamentos: IVendaResumoPDV['pagamentos'] }> = ({ pagamentos }) => {
  if (pagamentos.length === 0) return <Typography variant="caption" color="text.secondary">-</Typography>;

  const conteudo = (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
      {pagamentos.map((pagamento, index) => {
        const visual = getFormaPagamentoVisual(pagamento.categoria);
        return (
          <Chip
            key={index}
            size="small"
            label={pagamento.categoria}
            sx={{
              height: 22,
              fontSize: '11px',
              fontWeight: 600,
              color: visual.cor,
              bgcolor: alpha(visual.cor, 0.12),
            }}
          />
        );
      })}
    </Box>
  );

  if (pagamentos.length <= 1) return conteudo;

  return (
    <Tooltip
      title={(
        <Box>
          {pagamentos.map((pagamento, index) => (
            <Typography key={index} variant="caption" sx={{ display: 'block' }}>
              {pagamento.categoria}: {formatMoeda(pagamento.valor)}
            </Typography>
          ))}
        </Box>
      )}
    >
      {conteudo}
    </Tooltip>
  );
};

export const DialogResumoVendasPDV: React.FC<DialogResumoVendasPDVProps> = ({
  open, onClose, nomeCaixa, nomeOperador, dataabertura, colors, onImprimir,
  vendaPendenteItens, onRestaurarVendaPendente, onExcluirVendaPendente, onEditarVendaIniciada,
}) => {
  const isCompact = useMediaQuery('(max-height: 800px)');
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const [resumo, setResumo] = useState<IResumoVendasSessao | null>(null);
  const [atualizadoEm, setAtualizadoEm] = useState<Date | null>(null);
  const [busca, setBusca] = useState('');
  const [imprimindo, setImprimindo] = useState(false);
  // Venda aguardando usuário+senha de quem autoriza o cancelamento —
  // controla o DialogAutorizacaoGerente (null = fechado).
  const [vendaParaCancelar, setVendaParaCancelar] = useState<IVendaResumoPDV | null>(null);
  // Venda clicada na Relação de Vendas — abre DialogItensVendaPDV.
  const [vendaParaVerItens, setVendaParaVerItens] = useState<IVendaResumoPDV | null>(null);
  // idmov sendo carregado pro carrinho de edição no momento (botão "Editar
  // venda") — só pra desabilitar o próprio botão clicado enquanto busca os
  // itens, não precisa de autorização (ver onEditarVendaIniciada).
  const [carregandoEdicao, setCarregandoEdicao] = useState<string | null>(null);
  // Confirmação inline antes de excluir a venda pendente — troca os botões
  // do card por "tem certeza?" em vez de um window.confirm nativo, mais
  // consistente com o resto da tela.
  const [confirmandoExclusaoPendente, setConfirmandoExclusaoPendente] = useState(false);

  // Só os itens ainda ativos (não excluídos) contam pra "tem venda pendente
  // ou não" e pro total mostrado — mesmo critério usado pra decidir se
  // salva/restaura o carrinho em Pdv.tsx.
  const itensPendentesAtivos = vendaPendenteItens.filter((item) => !item.excluido);
  const totalVendaPendente = itensPendentesAtivos.reduce((acc, item) => acc + item.qtds * item.preco, 0);

  const carregarResumo = async () => {
    setCarregando(true);
    setErro('');
    const resultado = await SessaoCaixaService.getResumoVendas();
    setCarregando(false);

    if (resultado instanceof Error) {
      setErro(resultado.message);
      return;
    }
    setResumo(resultado);
    setAtualizadoEm(new Date());
  };

  useEffect(() => {
    if (open) {
      setBusca('');
      setConfirmandoExclusaoPendente(false);
      carregarResumo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const vendasFiltradas = useMemo(() => {
    const vendas = resumo?.vendas || [];
    const termo = busca.trim().toLowerCase();
    if (!termo) return vendas;

    return vendas.filter((venda) => {
      const numero = String(venda.numero ?? '').toLowerCase();
      const cliente = String(venda.nomeParceiro ?? '').toLowerCase();
      const formas = venda.pagamentos.map((p) => p.categoria).join(' ').toLowerCase();
      return numero.includes(termo) || cliente.includes(termo) || formas.includes(termo);
    });
  }, [resumo, busca]);

  const descontoMedioPorVenda = resumo && resumo.quantidadeVendas > 0
    ? resumo.descontos / resumo.quantidadeVendas
    : 0;

  const handleImprimirResumo = async () => {
    if (!resumo) return;
    setImprimindo(true);
    try {
      const blob = await PDFResumoVendasPDV({
        nomeCaixa,
        operador: nomeOperador,
        dataabertura,
        quantidadeVendas: resumo.quantidadeVendas,
        quantidadeItens: resumo.quantidadeItens,
        produtosDiferentes: resumo.produtosDiferentes,
        valorBruto: resumo.valorBruto,
        descontos: resumo.descontos,
        acrescimos: resumo.acrescimos,
        total: resumo.total,
        ticketMedio: resumo.ticketMedio,
        formasPagamento: resumo.formasPagamento,
        vendas: resumo.vendas.map((venda) => ({
          numero: venda.numero,
          emissao: venda.emissao,
          nomeParceiro: venda.nomeParceiro,
          quantidadeItens: venda.quantidadeItens,
          vprod: venda.vprod,
          desconto: venda.desconto,
          acrescimo: venda.acrescimo,
          total: venda.total,
          pagamentos: venda.pagamentos,
          status: venda.status,
        })),
      });
      onImprimir(blob, 'Resumo de vendas do PDV');
    } finally {
      setImprimindo(false);
    }
  };

  const periodoTexto = `${dayjs(dataabertura).format('DD/MM/YYYY HH:mm')} até ${dayjs(atualizadoEm ?? undefined).format('DD/MM/YYYY HH:mm')}`;

  const handleAutorizado = () => {
    setVendaParaCancelar(null);
    // A venda continua aqui, só marcada como cancelada — recarrega a lista.
    carregarResumo();
  };

  // "Editar venda" — só busca os itens (leitura, sem autorização nenhuma,
  // mesmo endpoint do "ver itens") e entrega pro Pdv.tsx carregar no
  // carrinho. Diferente de devolução: não cancela nem estorna nada aqui — o
  // update de verdade só acontece se/quando o operador finalizar a edição.
  const handleEditarVenda = async (venda: IVendaResumoPDV) => {
    setCarregandoEdicao(venda.idmov);
    const itens = await SessaoCaixaService.getItensVendaPdv(venda.idmov);
    setCarregandoEdicao(null);
    if (itens instanceof Error) {
      setErro(itens.message);
      return;
    }
    onEditarVendaIniciada(venda.idmov, venda.numero, itens);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      TransitionComponent={Zoom}
      TransitionProps={{ timeout: 220 }}
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: '16px',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: 'calc(100vh - 48px)',
          width: '94vw',
          maxWidth: '1360px',
        },
      }}
    >
      {/* CABEÇALHO */}
      <Box
        sx={{
          px: 2.5, py: isCompact ? 1.25 : 1.75, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          borderBottom: `0.5px solid ${alpha('#000', 0.08)}`,
          flexShrink: 0,
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <Box sx={{
            width: 34, height: 34, borderRadius: '8px', background: colors.primaryGradient, flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <BarChartRoundedIcon sx={{ fontSize: 18, color: colors.textOnPrimary }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={600} noWrap>Resumo de Vendas do PDV</Typography>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: { xs: 'none', sm: 'block' } }}>
              Confira todas as vendas realizadas, os totais por forma de pagamento e o resumo geral do período.
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <StorefrontRoundedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1 }}>Caixa</Typography>
              <Typography variant="body2" fontWeight={700} noWrap>{nomeCaixa || '—'}</Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <BadgeRoundedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1 }}>Operador</Typography>
              <Typography variant="body2" fontWeight={700} noWrap>{nomeOperador || '—'}</Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <EventRoundedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1 }}>Período</Typography>
              <Typography variant="body2" fontWeight={700} noWrap>{periodoTexto}</Typography>
            </Box>
          </Box>
          <IconButton size="small" onClick={onClose} sx={{ color: 'text.secondary' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* CONTEÚDO */}
      <Box sx={{ p: 2.5, overflowY: 'auto', minHeight: 0, flex: 1 }}>
        {/* VENDA PENDENTE — carrinho atual, ainda não finalizado (ver
            vendaPendenteItens/onRestaurarVendaPendente). Fica no topo, antes
            de qualquer outra coisa (mesmo enquanto o resumo da sessão ainda
            está carregando) — é a informação mais urgente dessa tela: uma
            venda com itens que ainda não virou lançamento nenhum. */}
        {itensPendentesAtivos.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 1.5,
              p: 1.75,
              mb: 2.5,
              borderRadius: '12px',
              border: '1px dashed #F57C00',
              bgcolor: alpha('#F57C00', 0.08),
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
              <Box sx={{
                width: 34, height: 34, borderRadius: '8px', bgcolor: alpha('#F57C00', 0.18), flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#F57C00',
              }}>
                <PendingActionsRoundedIcon sx={{ fontSize: 18 }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#B85C00' }}>
                  Venda pendente — ainda não finalizada
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {itensPendentesAtivos.length} {itensPendentesAtivos.length === 1 ? 'item' : 'itens'} no carrinho · {formatMoeda(totalVendaPendente)}
                  {' · '}não entra nos totais de venda desta tela
                </Typography>
              </Box>
            </Box>

            {confirmandoExclusaoPendente ? (
              // Confirmação inline — troca os botões normais por "tem
              // certeza?" em vez de excluir na hora do primeiro clique.
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                <Typography variant="caption" fontWeight={600} sx={{ color: '#B85C00' }}>
                  Excluir esta venda pendente?
                </Typography>
                <Button
                  size="small"
                  onClick={() => setConfirmandoExclusaoPendente(false)}
                  sx={{ color: 'text.secondary' }}
                >
                  Cancelar
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  color="error"
                  onClick={() => { onExcluirVendaPendente(); setConfirmandoExclusaoPendente(false); }}
                  sx={{ borderRadius: '10px' }}
                >
                  Excluir
                </Button>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                <Tooltip title="Excluir venda pendente">
                  <IconButton
                    size="small"
                    onClick={() => setConfirmandoExclusaoPendente(true)}
                    sx={{
                      color: '#B85C00',
                      bgcolor: alpha('#F57C00', 0.14),
                      '&:hover': { color: '#fff', bgcolor: '#D32F2F' },
                    }}
                  >
                    <DeleteOutlineRoundedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Button
                  variant="contained"
                  onClick={onRestaurarVendaPendente}
                  sx={{
                    borderRadius: '10px',
                    bgcolor: '#F57C00',
                    '&:hover': { bgcolor: '#E06C00' },
                  }}
                >
                  Continuar esta venda
                </Button>
              </Box>
            )}
          </Box>
        )}

        {carregando ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        ) : erro ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="error" variant="body2" sx={{ mb: 2 }}>{erro}</Typography>
            <Button variant="outlined" onClick={carregarResumo} sx={{ borderRadius: '10px' }}>
              Tentar novamente
            </Button>
          </Box>
        ) : resumo ? (
          <>
            {/* CARDS DE RESUMO */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2.5 }}>
              <CardMetrica label="Vendas realizadas" valor={String(resumo.quantidadeVendas)} icon={<ShoppingCartRoundedIcon sx={{ fontSize: 16 }} />} cor="#1976D2" />
              <CardMetrica label="Valor bruto" valor={formatMoeda(resumo.valorBruto)} icon={<PaidRoundedIcon sx={{ fontSize: 16 }} />} cor="#388E3C" />
              <CardMetrica label="Descontos" valor={formatMoeda(resumo.descontos)} icon={<LocalOfferRoundedIcon sx={{ fontSize: 16 }} />} cor="#D32F2F" />
              <CardMetrica label="Acréscimos" valor={formatMoeda(resumo.acrescimos)} icon={<AddCircleRoundedIcon sx={{ fontSize: 16 }} />} cor="#F57C00" />
              <CardMetrica label="Total vendido" valor={formatMoeda(resumo.total)} icon={<SavingsRoundedIcon sx={{ fontSize: 16 }} />} cor={colors.primarySolid} destaque />
            </Box>

            {/* TOTAIS POR FORMA DE PAGAMENTO + RESUMO GERAL */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2.5, flexWrap: 'wrap' }}>
              <Box sx={{ flex: '1 1 380px', minWidth: 280 }}>
                <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Totais por forma de pagamento
                </Typography>
                <Box sx={{ border: `0.5px solid ${alpha('#000', 0.1)}`, borderRadius: '10px', overflow: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Forma de pagamento</TableCell>
                        <TableCell align="right">Qtd. pagamentos</TableCell>
                        <TableCell align="right">Valor total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {resumo.formasPagamento.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={3}>
                            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 1.5 }}>
                              Nenhum pagamento registrado.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : resumo.formasPagamento.map((forma) => {
                        const visual = getFormaPagamentoVisual(forma.categoria);
                        return (
                          <TableRow key={forma.categoria}>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <Box sx={{ color: visual.cor, display: 'flex' }}>{visual.icon}</Box>
                                {forma.categoria}
                              </Box>
                            </TableCell>
                            <TableCell align="right">{forma.quantidade}</TableCell>
                            <TableCell align="right">{formatMoeda(forma.total)}</TableCell>
                          </TableRow>
                        );
                      })}
                      <TableRow sx={{ bgcolor: alpha(colors.primarySolid, 0.06) }}>
                        <TableCell sx={{ fontWeight: 700, color: colors.primarySolid }}>Total</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: colors.primarySolid }}>
                          {resumo.formasPagamento.reduce((acc, f) => acc + f.quantidade, 0)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: colors.primarySolid }}>
                          {formatMoeda(resumo.formasPagamento.reduce((acc, f) => acc + f.total, 0))}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                  A quantidade de pagamentos pode ser maior que a de vendas — uma venda pode ter mais de uma forma de pagamento.
                </Typography>
              </Box>

              <Box sx={{ flex: '1 1 300px', minWidth: 260 }}>
                <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Resumo geral
                </Typography>
                <Box sx={{ border: `0.5px solid ${alpha('#000', 0.1)}`, borderRadius: '10px', p: 1.75 }}>
                  {[
                    { icon: <Inventory2RoundedIcon sx={{ fontSize: 16 }} />, label: 'Total de itens vendidos', valor: String(resumo.quantidadeItens) },
                    { icon: <CategoryRoundedIcon sx={{ fontSize: 16 }} />, label: 'Produtos diferentes', valor: String(resumo.produtosDiferentes) },
                    { icon: <LocalOfferRoundedIcon sx={{ fontSize: 16 }} />, label: 'Desconto médio por venda', valor: formatMoeda(descontoMedioPorVenda) },
                  ].map((linha, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.6 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                        {linha.icon}
                        <Typography variant="body2" color="text.secondary">{linha.label}</Typography>
                      </Box>
                      <Typography variant="body2" fontWeight={600}>{linha.valor}</Typography>
                    </Box>
                  ))}
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PieChartRoundedIcon sx={{ fontSize: 16, color: colors.primarySolid }} />
                      <Typography variant="body2" fontWeight={700}>Total vendido (líquido)</Typography>
                    </Box>
                    <Typography variant="body1" fontWeight={700} sx={{ color: colors.primarySolid }}>
                      {formatMoeda(resumo.total)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* RELAÇÃO DE VENDAS */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="subtitle2" fontWeight={700}>
                Relação de vendas ({vendasFiltradas.length})
              </Typography>
              <TextField
                size="small"
                placeholder="Buscar por venda, cliente ou forma de pagamento..."
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                sx={{ minWidth: 260, flexGrow: { xs: 1, sm: 0 } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRoundedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Box sx={{ border: `0.5px solid ${alpha('#000', 0.1)}`, borderRadius: '10px', overflow: 'auto', maxHeight: 340 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Venda</TableCell>
                    <TableCell>Data/Hora</TableCell>
                    <TableCell>Cliente</TableCell>
                    <TableCell>Forma de pagamento</TableCell>
                    <TableCell align="right">Itens</TableCell>
                    <TableCell align="right">Subtotal</TableCell>
                    <TableCell align="right">Desconto</TableCell>
                    <TableCell align="right">Acréscimo</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell align="right">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {vendasFiltradas.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10}>
                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                          {resumo.vendas.length === 0 ? 'Nenhuma venda realizada nesta sessão.' : 'Nenhuma venda encontrada para essa busca.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : vendasFiltradas.map((venda, index) => {
                    const badge = venda.status ? BADGE_STATUS[venda.status] : null;
                    return (
                    <TableRow
                      key={venda.idmov}
                      onClick={() => setVendaParaVerItens(venda)}
                      sx={{
                        cursor: 'pointer',
                        opacity: venda.status ? 0.6 : 1,
                        '&:nth-of-type(even)': { backgroundColor: alpha(colors.primarySolid, 0.025) },
                        '&:hover': { backgroundColor: alpha(colors.primarySolid, 0.07) },
                      }}
                    >
                      <TableCell sx={{ fontWeight: 600 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          #{venda.numero}
                          {venda.modificada && (
                            <Tooltip title="Editada fora do PDV depois de criada">
                              <WarningAmberRoundedIcon sx={{ fontSize: 15, color: '#F57C00' }} />
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>{dayjs(venda.emissao).format('DD/MM/YYYY HH:mm')}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <PersonOutlineRoundedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                          {venda.nomeParceiro}
                        </Box>
                      </TableCell>
                      <TableCell><ChipsPagamento pagamentos={venda.pagamentos} /></TableCell>
                      <TableCell align="right">{venda.quantidadeItens}</TableCell>
                      <TableCell align="right">{formatMoeda(venda.vprod)}</TableCell>
                      <TableCell align="right">{formatMoeda(venda.desconto)}</TableCell>
                      <TableCell align="right">{formatMoeda(venda.acrescimo)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{formatMoeda(venda.total)}</TableCell>
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        {badge ? (
                          <Tooltip title={venda.nomeAutorizador ? `Por ${venda.nomeAutorizador} em ${dayjs(venda.statusem).format('DD/MM/YYYY HH:mm')}` : ''}>
                            <Chip
                              size="small"
                              label={badge.label}
                              sx={{ height: 22, fontSize: '11px', fontWeight: 700, color: badge.cor, bgcolor: alpha(badge.cor, 0.12) }}
                            />
                          </Tooltip>
                        ) : (
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                            <Tooltip title="Editar venda — adicionar item ou trocar forma de pagamento (atualiza esta venda, não cria outra)">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditarVenda(venda)}
                                  disabled={carregandoEdicao === venda.idmov}
                                  sx={{ color: colors.primarySolid }}
                                >
                                  <EditRoundedIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Cancelar venda">
                              <IconButton size="small" onClick={() => setVendaParaCancelar(venda)} sx={{ color: '#D32F2F' }}>
                                <CancelRoundedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          </>
        ) : null}
      </Box>

      {/* RODAPÉ */}
      <Box
        sx={{
          px: 2.5, py: 1.5, borderTop: `0.5px solid ${alpha('#000', 0.08)}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap',
          flexShrink: 0,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600} noWrap>
            {resumo ? `${resumo.quantidadeVendas} venda${resumo.quantidadeVendas === 1 ? '' : 's'} • ${resumo.quantidadeItens} ite${resumo.quantidadeItens === 1 ? 'm' : 'ns'} vendido${resumo.quantidadeItens === 1 ? '' : 's'}` : ''}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            Período: {periodoTexto}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={handleImprimirResumo}
            disabled={!resumo || carregando || imprimindo}
            sx={{ borderRadius: '10px' }}
          >
            {imprimindo ? 'Gerando...' : 'Imprimir resumo'}
          </Button>
          <Button
            variant="contained"
            onClick={onClose}
            sx={{
              borderRadius: '10px',
              background: colors.primaryGradient,
              color: colors.textOnPrimary,
              '&:hover': { background: colors.hoverGradient },
            }}
          >
            Fechar
          </Button>
        </Box>
      </Box>

      <DialogAutorizacaoGerente
        open={!!vendaParaCancelar}
        onClose={() => setVendaParaCancelar(null)}
        colors={colors}
        corDestaque="#D32F2F"
        titulo={`Cancelar venda #${vendaParaCancelar?.numero}`}
        mensagem={`A venda #${vendaParaCancelar?.numero} (${formatMoeda(vendaParaCancelar?.total ?? 0)}) será cancelada e o caixa estornado. Informe usuário e senha de quem autoriza.`}
        onConfirmar={async (login, senha) => {
          if (!vendaParaCancelar) return new Error('Nenhuma venda selecionada.');
          return SessaoCaixaService.cancelarVendaPdv(vendaParaCancelar.idmov, login, senha);
        }}
        onAutorizado={handleAutorizado}
      />

      <DialogItensVendaPDV
        open={!!vendaParaVerItens}
        onClose={() => setVendaParaVerItens(null)}
        venda={vendaParaVerItens}
        colors={colors}
      />
    </Dialog>
  );
};
